import styled, { css } from 'styled-components';

export const TabBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.level2};
  margin-bottom: ${({ theme }) => theme.spacing.level5};
  max-width: 800px;

  ${({ theme: { colors, spacing } }) => css`
    border-bottom: 1px solid ${colors.outlineVariant};
    padding-bottom: ${spacing.level1};
  `}
`;

export const Tab = styled.button<{ $active?: boolean }>`
  all: unset;
  cursor: pointer;
  position: relative;
  padding: ${({ theme }) => `${theme.spacing.level2} ${theme.spacing.level4}`};
  transition: color 0.2s;

  ${({ theme: { colors, font }, $active }) => css`
    font-size: ${font.bodyMedium.fontSize};
    font-weight: ${$active ? 600 : 400};
    color: ${$active ? colors.primary : colors.onSurfaceVariant};

    &:focus-visible {
      outline: 2px solid ${colors.primary};
      outline-offset: 2px;
    }

    &:hover {
      color: ${colors.primary};
    }

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: -1px;
      height: 2px;
      background: ${$active ? colors.primary : 'transparent'};
      border-radius: 1px;
      transition: background 0.2s;
    }
  `}
`;

export const ReadOnlyBanner = styled.div`
  max-width: 800px;
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.spacing.level4};

  ${({ theme: { colors, font, spacing, borderRadius } }) => css`
    font-size: ${font.bodySmall.fontSize};
    color: ${colors.onSurfaceVariant};
    background: ${colors.surfaceContainerHighest};
    border-radius: ${borderRadius.small};
    padding: ${spacing.level3} ${spacing.level4};
  `}
`;

export const Description = styled.p`
  max-width: 640px;
  line-height: 1.5;
  margin: 0 0 ${({ theme }) => theme.spacing.level5} 0;

  ${({ theme: { colors, font } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurfaceVariant};
  `}
`;

export const InstructionsCard = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 800px;
  width: 100%;
  overflow: hidden;

  ${({ theme: { colors, spacing, borderRadius } }) => css`
    background: ${colors.surfaceContainer};
    border-radius: ${borderRadius.large};
    padding: ${spacing.level5};
    gap: ${spacing.level4};
  `}

  @media (max-width: 768px) {
    ${({ theme: { spacing } }) => css`
      padding: ${spacing.level4};
    `}
  }
`;

export const InstructionsTextArea = styled.textarea`
  width: 100%;
  min-height: 300px;
  resize: vertical;
  border: none;
  outline: none;
  background: transparent;
  line-height: 1.6;

  ${({ theme: { colors, font, spacing, borderRadius } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    font-family: ${font.bodyMedium.fontFamily || 'inherit'};
    color: ${colors.onSurface};
    padding: ${spacing.level4};
    border-radius: ${borderRadius.small};

    &::placeholder {
      color: ${colors.onSurfaceVariant};
      opacity: 0.6;
    }

    &:focus {
      box-shadow: inset 0 0 0 1px ${colors.primary};
    }
  `}
`;

export const InstructionsFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;

  ${({ theme: { spacing } }) => css`
    padding-top: ${spacing.level3};
    gap: ${spacing.level3};
  `}
`;

export const CharacterCount = styled.span<{ $warning?: boolean }>`
  ${({ theme: { colors, font }, $warning }) => css`
    font-size: ${font.bodySmall.fontSize};
    color: ${$warning ? colors.error : colors.onSurfaceVariant};
    opacity: ${$warning ? 1 : 0.7};
    transition: color 0.2s;
  `}
`;

const centeredMessage = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.level4};
  padding: ${({ theme }) => theme.spacing.level8} 0;
  font-size: ${({ theme }) => theme.font.bodyMedium.fontSize};
`;

export const ErrorContainer = styled.div`
  ${centeredMessage}
  color: ${({ theme }) => theme.colors.error};
`;

export const LoadingContainer = styled.div`
  ${centeredMessage}
  color: ${({ theme }) => theme.colors.onSurfaceVariant};
`;
